import { Divider, Flex, Group, Select } from '@byecode/ui'
import {
    emptyBoolField,
    emptyTextField,
    type FlowNode,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    type NodeTypes,
    OtherPlatformNodeType,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData,
    type WechatUploadShippingInfoActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { WechatPaymentAccountSelect } from '@/components/WechatPaymentAccountSelect'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import OutputVariable from '../../../OutputVariable'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as CM from '../../styles'
import { SelectMultipleRecord } from '../component/SelectMultipleRecord'
import {
    DELIVERY_MODE_OPTIONS,
    DELIVERY_MODE_SPLIT,
    LOGISTICS_TYPE_EXPRESS,
    LOGISTICS_TYPE_OPTIONS,
    ORDER_NUMBER_TYPE_OPTIONS,
    ORDER_NUMBER_TYPE_OUT_TRADE_NO
} from '../wechatShipping/constants'
import { DeliveryCompanyField } from '../wechatShipping/DeliveryCompanyField'

interface WechatUploadShippingInfoActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: WechatUploadShippingInfoActionConfig
    onChange: (value: WechatUploadShippingInfoActionConfig) => void
}

export const WechatUploadShippingInfoActionConfigure: React.FC<WechatUploadShippingInfoActionConfigureProps> = ({
    nodeType,
    allParentNodes,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const otherPlatformList = useAtomData(otherPlatformListAtom)
    const userOption = useGetUserRecordOptions()
    const miniProgramAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'WECHAT_MINI_PROGRAM'), [otherPlatformList])

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)),
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const isOutTradeNo = (value.orderNumberType ?? ORDER_NUMBER_TYPE_OUT_TRADE_NO) === ORDER_NUMBER_TYPE_OUT_TRADE_NO
    const isExpress = (value.logisticsType ?? LOGISTICS_TYPE_EXPRESS) === LOGISTICS_TYPE_EXPRESS
    const isSplit = value.deliveryMode === DELIVERY_MODE_SPLIT

    const header = (
        <Group label="动作配置">
            <ListItem4ByecodeUi>
                <CM.FormItemLabelWrapper>
                    <CM.FormItemLabel>类型</CM.FormItemLabel>
                </CM.FormItemLabelWrapper>
                <CM.FormItemContent>
                    {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                </CM.FormItemContent>
            </ListItem4ByecodeUi>
        </Group>
    )

    if (!miniProgramAccount) {
        return (
            <>
                {header}
                <UnitNoAuth type="WECHAT_MINI_PROGRAM" />
            </>
        )
    }

    return (
        <CM.Container>
            {header}
            <WechatPaymentAccountSelect
                value={value.paymentAccountId}
                onChange={paymentAccountId => onChange({ ...value, paymentAccountId })}
            />
            <Divider />
            <Group label="订单信息">
                <CM.FieldList>
                    <CM.FieldItem>
                        <CM.FieldTitle required>单号类型</CM.FieldTitle>
                        <Select
                            options={ORDER_NUMBER_TYPE_OPTIONS}
                            value={String(value.orderNumberType ?? ORDER_NUMBER_TYPE_OUT_TRADE_NO)}
                            onChange={orderNumberType => onChange({ ...value, orderNumberType: Number(orderNumberType) })}
                        />
                    </CM.FieldItem>
                    {isOutTradeNo ? (
                        <CM.FieldItem>
                            <CM.FieldTitle required>商户订单号</CM.FieldTitle>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                disabled={disabledWithVersion}
                                field={emptyTextField}
                                value={value.outTradeNo}
                                onBlur={outTradeNo => onChange({ ...value, outTradeNo })}
                                autoHeight
                                placeholder="请输入商户订单号"
                                userOption={userOption}
                                upstreamOptions={textUpstreamOptions}
                                dataSourceList={dataSourceList}
                            />
                        </CM.FieldItem>
                    ) : (
                        <CM.FieldItem>
                            <CM.FieldTitle required>微信支付单号</CM.FieldTitle>
                            <NodeFieldPreview
                                innerTypes={getInputInnerTypeMatch('TEXT')}
                                disabled={disabledWithVersion}
                                field={emptyTextField}
                                value={value.transactionId}
                                onBlur={transactionId => onChange({ ...value, transactionId })}
                                autoHeight
                                placeholder="请输入微信支付单号"
                                userOption={userOption}
                                upstreamOptions={textUpstreamOptions}
                                dataSourceList={dataSourceList}
                            />
                        </CM.FieldItem>
                    )}
                    <CM.FieldItem>
                        <Flex gap={16}>
                            <CM.FieldTitle>支付者 openid</CM.FieldTitle>
                        </Flex>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.openid}
                            onBlur={openid => onChange({ ...value, openid })}
                            autoHeight
                            placeholder="未填时按商户单号从支付记录回填"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                </CM.FieldList>
            </Group>
            <Divider />
            <Group label="发货信息">
                <CM.FieldList>
                    <CM.FieldItem>
                        <CM.FieldTitle required>物流模式</CM.FieldTitle>
                        <Select
                            options={LOGISTICS_TYPE_OPTIONS}
                            value={String(value.logisticsType ?? LOGISTICS_TYPE_EXPRESS)}
                            onChange={logisticsType => onChange({ ...value, logisticsType: Number(logisticsType) })}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle required>发货模式</CM.FieldTitle>
                        <Select
                            options={DELIVERY_MODE_OPTIONS}
                            value={String(value.deliveryMode ?? 1)}
                            onChange={deliveryMode => onChange({ ...value, deliveryMode: Number(deliveryMode) })}
                        />
                    </CM.FieldItem>
                    {isSplit && (
                        <>
                            <SelectMultipleRecord
                                description="从上游查找记录节点映射物流列表，最多 15 条。"
                                value={value.shippingListNodeId}
                                onChange={shippingListNodeId => onChange({ ...value, shippingListNodeId })}
                                allParentNodes={allParentNodes}
                            />
                            <CM.FieldItem>
                                <CM.FieldTitle required>是否已全部发货</CM.FieldTitle>
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('BOOL')}
                                    disabled={disabledWithVersion}
                                    field={emptyBoolField}
                                    value={value.isAllDelivered}
                                    onBlur={isAllDelivered => onChange({ ...value, isAllDelivered })}
                                    autoHeight
                                    userOption={userOption}
                                    upstreamOptions={textUpstreamOptions}
                                    dataSourceList={dataSourceList}
                                />
                            </CM.FieldItem>
                        </>
                    )}
                    <CM.FieldItem>
                        <CM.FieldTitle required>商品描述</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.itemDesc}
                            onBlur={itemDesc => onChange({ ...value, itemDesc })}
                            autoHeight
                            placeholder="请输入商品描述"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    {isExpress && (
                        <>
                            <CM.FieldItem>
                                <CM.FieldTitle required>物流单号</CM.FieldTitle>
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    disabled={disabledWithVersion}
                                    field={emptyTextField}
                                    value={value.trackingNo}
                                    onBlur={trackingNo => onChange({ ...value, trackingNo })}
                                    autoHeight
                                    placeholder="请输入物流单号"
                                    userOption={userOption}
                                    upstreamOptions={textUpstreamOptions}
                                    dataSourceList={dataSourceList}
                                />
                            </CM.FieldItem>
                            <DeliveryCompanyField
                                allParentNodes={allParentNodes}
                                required
                                label="物流公司编码"
                                value={value.expressCompany}
                                onChange={expressCompany => onChange({ ...value, expressCompany })}
                            />
                            <CM.FieldItem>
                                <CM.FieldTitle>收件人联系方式</CM.FieldTitle>
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    disabled={disabledWithVersion}
                                    field={emptyTextField}
                                    value={value.receiverContact}
                                    onBlur={receiverContact => onChange({ ...value, receiverContact })}
                                    autoHeight
                                    placeholder="顺丰时与寄件人联系方式必填其一"
                                    userOption={userOption}
                                    upstreamOptions={textUpstreamOptions}
                                    dataSourceList={dataSourceList}
                                />
                            </CM.FieldItem>
                            <CM.FieldItem>
                                <CM.FieldTitle>寄件人联系方式</CM.FieldTitle>
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    disabled={disabledWithVersion}
                                    field={emptyTextField}
                                    value={value.consignorContact}
                                    onBlur={consignorContact => onChange({ ...value, consignorContact })}
                                    autoHeight
                                    placeholder="顺丰时与收件人联系方式必填其一"
                                    userOption={userOption}
                                    upstreamOptions={textUpstreamOptions}
                                    dataSourceList={dataSourceList}
                                />
                            </CM.FieldItem>
                        </>
                    )}
                </CM.FieldList>
            </Group>
            <Divider />
            <OutputVariable nodeType={OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO} />
        </CM.Container>
    )
}
